import { Suspense, useEffect, useRef } from 'react'
import type { AppConfig } from '../types'
import type { Action, BookingState, Step } from '../booking/state'
import { flowSteps, isProviderBased } from '../booking/state'
import { formatDateLong, formatTime12 } from '../dates'
import { ServiceStep } from './ServiceStep'
import { SubProfileStep } from './SubProfileStep'
import { PatientTypeStep } from './PatientTypeStep'
import { RegionStep } from './RegionStep'
import { SlotPicker } from './SlotPicker'
import { DetailsForm, DetailsFormFallback } from './DetailsFormLazy'
import { Confirmation } from './Confirmation'
import type { ToastMsg } from './Toast'

// ---------------------------------------------------------------------------
// Single-page (accordion) flow — every reached step stacks on one scrolling
// page. Completed steps collapse to a summary with "Change"; the active step is
// expanded. Same reducer/state as the wizard (behavioral-health-style flow).
// ---------------------------------------------------------------------------

export function SinglePageFlow({
  config,
  state,
  dispatch,
  notify
}: {
  config: AppConfig
  state: BookingState
  dispatch: React.Dispatch<Action>
  notify: (m: ToastMsg) => void
}) {
  const activeRef = useRef<HTMLDivElement | null>(null)
  const prevStep = useRef<Step | 'confirmation'>('service')
  const providerBased = isProviderBased(config, state.profile)

  // Scroll the newly-active section into view as the user progresses, and move
  // focus to its heading so keyboard/screen-reader users follow the transition.
  useEffect(() => {
    if (prevStep.current !== state.step && prevStep.current !== 'service') {
      activeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      const el = activeRef.current?.querySelector<HTMLElement>('h1.step-heading')
      if (el) {
        el.setAttribute('tabindex', '-1')
        el.focus({ preventScroll: true })
      }
    }
    prevStep.current = state.step
  }, [state.step])

  if (state.step === 'confirmation') {
    return <Confirmation config={config} state={state} dispatch={dispatch} />
  }

  const steps = flowSteps(config, state)
  const currentIdx = steps.indexOf(state.step)
  const noop = () => {}

  const active = (step: Step) => {
    switch (step) {
      case 'service':
        return <ServiceStep config={config} dispatch={dispatch} />
      case 'subProfile':
        return <SubProfileStep config={config} state={state} dispatch={dispatch} onBack={noop} embedded />
      case 'patientType':
        return <PatientTypeStep config={config} state={state} dispatch={dispatch} onBack={noop} embedded />
      case 'region':
        return <RegionStep config={config} dispatch={dispatch} onBack={noop} embedded />
      case 'slots':
        return (
          <SlotPicker
            config={config}
            state={state}
            dispatch={dispatch}
            onBack={noop}
            providerBased={providerBased}
            embedded
          />
        )
      case 'details':
        return (
          <Suspense fallback={<DetailsFormFallback />}>
            <DetailsForm config={config} state={state} dispatch={dispatch} notify={notify} onBack={noop} embedded />
          </Suspense>
        )
      default:
        return null
    }
  }

  return (
    <div className="single-page">
      {steps.map((step, i) => {
        if (i > currentIdx) return null
        if (i < currentIdx) {
          return (
            <SummaryRow
              key={step}
              label={summaryLabel(step)}
              value={summaryValue(step, state)}
              onEdit={() => dispatch({ type: 'EDIT_STEP', step })}
            />
          )
        }
        return (
          <div className="sp-section" key={step} ref={activeRef}>
            {active(step)}
          </div>
        )
      })}
    </div>
  )
}

// A completed step, collapsed to a summary. The whole row is tappable — click
// anywhere to jump back and edit that step (easier than a small "Change" button).
function SummaryRow({ label, value, onEdit }: { label: string; value: string; onEdit: () => void }) {
  return (
    <button type="button" className="sp-summary" onClick={onEdit} aria-label={`Edit ${label}: ${value}`}>
      <span className="sp-summary-check" aria-hidden>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12.5l4.5 4.5L19 7" />
        </svg>
      </span>
      <span className="sp-summary-text">
        <span className="sp-summary-label">{label}</span>
        <span className="sp-summary-value">{value}</span>
      </span>
      <span className="sp-summary-edit" aria-hidden>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 20h4L18.5 9.5a2 2 0 0 0-3-3L5 17v3z" />
          <path d="M13.5 6.5l3 3" />
        </svg>
        Edit
      </span>
    </button>
  )
}

function summaryLabel(step: Step): string {
  switch (step) {
    case 'service':
      return 'Service'
    case 'subProfile':
      return 'Visit type'
    case 'patientType':
      return 'Patient'
    case 'region':
      return 'Location'
    case 'slots':
      return 'Provider & time'
    default:
      return ''
  }
}

function summaryValue(step: Step, state: BookingState): string {
  switch (step) {
    case 'service':
      return [state.profile?.name, state.profile?.name_line_2].filter(Boolean).join(' ')
    case 'subProfile':
      return state.subProfile?.name ?? ''
    case 'patientType':
      return state.status === 'new' ? 'New patient' : 'Existing patient'
    case 'region':
      return state.region?.name ?? ''
    case 'slots':
      return state.slot
        ? `${state.slot.entity.name} · ${formatDateLong(state.slot.date)} · ${formatTime12(state.slot.time)}`
        : ''
    default:
      return ''
  }
}
